import { useState } from 'react'
import { Button } from 'blaze-ui/Button'
import { useModals } from 'almanac-ui/Modal'
import { FontPairingsModal } from 'components/FontPairingsModal'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useFontPairings } from 'hooks/queries/fontPairings'
import { useFonts } from 'hooks/queries/workspaces/useFonts'
import { deleteFont } from 'hooks/queries/workspaces/useDeleteFont'
import { useUpsertTextStyle } from 'hooks/queries/workspaces/brandVisualKits/useUpsertTextStyle'
import { useUpdateBrandVisualKit } from 'hooks/queries/workspaces/useUpdateBrandVisualKit'
import { FontRole, FontStyle } from 'types'
import { useQueryClient } from 'react-query'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import Styles from './BrandVisualStyleFonts.module.scss'
import SharedStyles from '../BrandVisualStyleSelection/BrandVisualStyleSelection.module.scss'
import { StyleGenerated } from '../BrandVisualStyleSelection/GenerationStage'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { FontPairingCard, FontPairingCardSkeleton } from 'components/FontPairingCard/FontPairingCard'
import { FontPairing } from 'hooks/queries/fontPairings'
import { VisualStylePreview } from 'components/VisualStylePreview/VisualStylePreview'
import { hexToRgb } from 'utils/colorHelpers'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'

export const isTooContrasty = (hexColor: string): boolean => {
  const rgb = hexToRgb(hexColor)
  if (!rgb) return false

  // Calculate relative luminance using sRGB color space
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
  return luminance > 0.9 || luminance < 0.1
}

export interface BrandVisualStyleFontsProps {
  onNext: (selectedFontPairingId: number | null) => void
  onBack: () => void
  brandKitId?: number
  initialFontPairings: FontPairing[]
  selectedStyle: StyleGenerated
}

export const BrandVisualStyleFonts = ({
  onNext,
  onBack,
  brandKitId,
  initialFontPairings,
  selectedStyle,
}: BrandVisualStyleFontsProps) => {
  const { openModal } = useModals()
  const queryClient = useQueryClient()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const [fontPairings, setFontPairings] = useState<FontPairing[]>(initialFontPairings)
  const [selectedFontPairingId, setSelectedFontPairingId] = useState<number | null>(initialFontPairings[0].id)
  const [isSaving, setIsSaving] = useState(false)

  const selectedVisualStyleId = selectedStyle.id

  // Fetch brand kit to get brand_visual_kit
  const { data: brandKitData } = useBrandKit(brandKitId, { enabled: !!brandKitId })
  const brandVisualKit = brandKitData?.brand_kit?.brand_visual_kit
  const brandVisualKitId = brandVisualKit?.id

  // Get the primary brand color from the brand kit's color palette
  const brandColors = brandVisualKit?.primary_color_palette?.colors
  const brandColor = brandColors?.find((color) => !isTooContrasty(color.hex))?.hex || brandColors?.[0]?.hex

  // Fetch font pairings to get font configs
  const { data: fontPairingsData, isLoading: isLoadingFontPairings } = useFontPairings()

  // Fetch custom fonts to detect if user has uploaded any
  const { data: fontsData } = useFonts()
  const customFonts = fontsData?.fonts ?? []
  const hasCustomFonts = customFonts.length > 0

  // Delete all custom fonts (used when user selects a font pairing instead)
  const deleteAllCustomFonts = async () => {
    if (customFonts.length === 0) return
    await Promise.all(customFonts.map((font) => deleteFont({ workspaceId: currentWorkspaceId, id: font.id })))
    queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'fonts', 'list'])
  }

  // Mutations for saving
  const { mutateAsync: updateBrandVisualKit } = useUpdateBrandVisualKit({
    id: brandVisualKitId as number,
  })
  const { mutateAsync: upsertTitleStyle } = useUpsertTextStyle({
    brandVisualKitId: brandVisualKitId as number,
    fontRole: FontRole.Title,
  })
  const { mutateAsync: upsertBodyStyle } = useUpsertTextStyle({
    brandVisualKitId: brandVisualKitId as number,
    fontRole: FontRole.Body,
  })

  const handleOpenFontPairingsModal = () => {
    openModal(FontPairingsModal, {
      selectedFontPairingId,
      title: 'Font Styles',
      onConfirm: async (newFontPairingId: number) => {
        // Delete custom fonts when user selects a font pairing
        await deleteAllCustomFonts()
        setSelectedFontPairingId(newFontPairingId)

        // If selected font pairing is not in the displayed ones, replace the first with it
        if (selectedFontPairingId !== null && !fontPairings.some((fp) => fp.id === newFontPairingId)) {
          const newFontPairing = fontPairingsData?.find((fp) => fp.id === newFontPairingId)

          if (newFontPairing) {
            setFontPairings([newFontPairing, ...fontPairings.slice(0, 2)])
          }
        }
      },
    })
  }

  const handleNext = async () => {
    setIsSaving(true)
    const tasks: Promise<unknown>[] = []

    // Save visual style - pass the full brand visual kit record with the new visual_style_id
    if (selectedVisualStyleId && brandVisualKit) {
      tasks.push(updateBrandVisualKit({ ...brandVisualKit, visual_style_id: selectedVisualStyleId }))
    }

    // Save font pairing as text styles (same approach as BrandKitConfirm)
    if (selectedFontPairingId && fontPairingsData) {
      const selectedPairing = fontPairingsData.find((fp) => fp.id === selectedFontPairingId)
      if (selectedPairing) {
        if (selectedPairing.header_font_config) {
          const headerConfig = selectedPairing.header_font_config
          tasks.push(
            upsertTitleStyle({
              style: {
                font_family: headerConfig.family,
                font_weight: headerConfig.weight as FontStyle['font_weight'],
                letter_spacing: headerConfig.letter_spacing,
                line_height: headerConfig.line_height,
                italic: headerConfig.italic,
                uppercase: headerConfig.uppercase,
              },
              name: headerConfig.family,
            }),
          )
        }
        if (selectedPairing.body_font_config) {
          const bodyConfig = selectedPairing.body_font_config
          tasks.push(
            upsertBodyStyle({
              style: {
                font_family: bodyConfig.family,
                font_weight: bodyConfig.weight as FontStyle['font_weight'],
                letter_spacing: bodyConfig.letter_spacing,
                line_height: bodyConfig.line_height,
                italic: bodyConfig.italic,
                uppercase: bodyConfig.uppercase,
              },
              name: bodyConfig.family,
            }),
          )
        }
      }
    }

    if (tasks.length > 0) {
      await Promise.all(tasks)
      await queryClient.refetchQueries(['workspaces', currentWorkspaceId, 'brandKits'])
    }

    setIsSaving(false)
    onNext(selectedFontPairingId)
  }

  return (
    <div className={SharedStyles.selectImageStep}>
      <div className={SharedStyles.leftSide}>
        <div className={SharedStyles.title}>
          <Heading level={1} color="var(--dark-80)">
            Select a brand font
          </Heading>
          <Text variant="primary" color="var(--dark-40)">
            Based on your brand, we recommend:
          </Text>
        </div>

        <div className={cx(SharedStyles.content, Styles.content)}>
          <div className={Styles.fontPairings}>
            {isLoadingFontPairings && [1, 2, 3].map((i) => <FontPairingCardSkeleton key={i} />)}
            {!isLoadingFontPairings && hasCustomFonts && (
              <div className={Styles.customFontCard}>
                <Text variant="primary" color="var(--dark-80)">
                  Custom Font Uploaded
                </Text>
                <Text variant="label" color="var(--dark-40)">
                  Your uploaded fonts will be used for social posts
                </Text>
              </div>
            )}
            {!isLoadingFontPairings &&
              !hasCustomFonts &&
              fontPairings.map((fontPairing) => (
                <FontPairingCard
                  key={fontPairing.id}
                  fontPairing={fontPairing}
                  isSelected={selectedFontPairingId === fontPairing.id}
                  onSelect={() => setSelectedFontPairingId(fontPairing.id)}
                />
              ))}
          </div>
          <div className={Styles.actions}>
            <Button size="lg" variant="secondary" onPress={handleOpenFontPairingsModal}>
              Browse all fonts
            </Button>
          </div>
        </div>
        <div className={SharedStyles.footer}>
          <Button variant="tertiary" size="lg" onPress={onBack} isDisabled={isSaving}>
            Back
          </Button>
          <Button size="lg" onPress={handleNext} isDisabled={isSaving}>
            {isSaving ? (
              <>
                Saving <LoadingEllipses />
              </>
            ) : (
              'Looks Good!'
            )}
          </Button>
        </div>
      </div>
      <div
        className={cx(SharedStyles.transformedImageContainer, Styles.transformedImageContainer, {
          [Styles.loading]: false,
        })}
      >
        <VisualStylePreview
          fontPairingId={selectedFontPairingId}
          brandColor={brandColor}
          styleGenerated={selectedStyle}
        />
      </div>
    </div>
  )
}
